import { useMemo } from 'react';
import { baseGridDefaults, defaultPageSize } from './gridDefaults';
import type { GridColumnWidthModel } from './gridState';
import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid';

type UrlState = {
  page?: number;
  pageSize?: number;
  sortModel?: GridSortModel;
  filterModel?: GridFilterModel;
} | null;

type UseGridInitialStateParams<T extends GridValidRowModel> = {
  columns: ReadonlyArray<GridColDef<T>>;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  urlState: UrlState;
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  restoreFilterModel?: boolean;
  columnVisibilityModel?: GridColumnVisibilityModel;
  columnVisibilityPresets?: Record<string, GridColumnVisibilityModel>;
  columnVisibilityPreset?: string;
};

type UseGridInitialStateResult = {
  resolvedPageSizeOptions: number[];
  resolvedInitialPageSize: number;
  initialPaginationModel: GridPaginationModel;
  initialSortModel: GridSortModel;
  initialFilterModel: GridFilterModel;
  initialColumnVisibilityModel: GridColumnVisibilityModel;
  initialColumnWidthModel: GridColumnWidthModel;
};

export const useGridInitialState = <T extends GridValidRowModel>({
  columns,
  pageSizeOptions,
  initialPageSize,
  urlState,
  initialSortModel: initialSortModelProp,
  initialFilterModel: initialFilterModelProp,
  restoreFilterModel = true,
  columnVisibilityModel,
  columnVisibilityPresets,
  columnVisibilityPreset,
}: UseGridInitialStateParams<T>): UseGridInitialStateResult => {
  const resolvedPageSizeOptions = useMemo(() => {
    const fallbackOptions = baseGridDefaults.pageSizeOptions;
    if (!pageSizeOptions || pageSizeOptions.length === 0) {
      return fallbackOptions;
    }
    const uniqueValidOptions = Array.from(
      new Set(pageSizeOptions.filter((value) => Number.isFinite(value) && value > 0)),
    );
    if (uniqueValidOptions.length === 0) {
      return fallbackOptions;
    }
    return uniqueValidOptions;
  }, [pageSizeOptions]);
  const resolvedInitialPageSize = useMemo(() => {
    if (initialPageSize && resolvedPageSizeOptions.includes(initialPageSize)) {
      return initialPageSize;
    }
    if (resolvedPageSizeOptions.includes(defaultPageSize)) {
      return defaultPageSize;
    }
    return resolvedPageSizeOptions[0] ?? defaultPageSize;
  }, [initialPageSize, resolvedPageSizeOptions]);

  const isValidPageSize = (value: number | undefined): value is number =>
    typeof value === 'number' && Number.isFinite(value) && value > 0;
  const normalisePageSize = (value: number | undefined): number => {
    if (isValidPageSize(value) && resolvedPageSizeOptions.includes(value)) {
      return value;
    }
    return resolvedInitialPageSize;
  };
  const normaliseSortModel = (model: GridSortModel): GridSortModel => {
    if (model.length <= 1) return model;
    return [model[0]];
  };

  const initialSortModel = normaliseSortModel(
    urlState?.sortModel && Array.isArray(urlState.sortModel)
      ? urlState.sortModel
      : (initialSortModelProp ?? baseGridDefaults.initialSortModel ?? []),
  );
  const restoredFilterModel =
    restoreFilterModel && urlState?.filterModel && Array.isArray(urlState.filterModel.items)
      ? urlState.filterModel
      : null;
  const initialFilterModel: GridFilterModel = restoredFilterModel ??
    initialFilterModelProp ??
    baseGridDefaults.initialFilterModel ?? { items: [] };

  const autoCompactPreset = useMemo(() => {
    const model: GridColumnVisibilityModel = {};
    columns.forEach((column) => {
      if (column.field === 'description' || column.field === 'projectName') {
        model[column.field] = false;
      }
    });
    return Object.keys(model).length > 0 ? model : null;
  }, [columns]);

  const resolvedPresets =
    columnVisibilityPresets ??
    (autoCompactPreset ? { default: {}, compact: autoCompactPreset } : undefined);
  const resolvedPresetKey =
    columnVisibilityPreset ??
    (resolvedPresets ? Object.keys(resolvedPresets)[0] : undefined) ??
    null;
  const resolvedPresetModel =
    resolvedPresetKey && resolvedPresets ? resolvedPresets[resolvedPresetKey] : undefined;
  const initialColumnVisibilityModel = columnVisibilityModel ?? resolvedPresetModel ?? {};
  const initialColumnWidthModel: GridColumnWidthModel = {};

  const initialPageState = Number.isFinite(urlState?.page) ? Number(urlState?.page) : 0;
  const initialPageSizeState = normalisePageSize(urlState?.pageSize ?? resolvedInitialPageSize);

  return {
    resolvedPageSizeOptions,
    resolvedInitialPageSize,
    initialPaginationModel: { page: initialPageState, pageSize: initialPageSizeState },
    initialSortModel,
    initialFilterModel,
    initialColumnVisibilityModel,
    initialColumnWidthModel,
  };
};
